/**
 * Custom Hooks for Crew Certificates
 * Provides React hooks for managing crew certificate data with expiry tracking
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { crewCertificatesService } from '../services';
import { CrewCertificate } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all crew certificates
 */
export function useCrewCertificates(
  options?: ResourceQueryOptions
): ResourceState<CrewCertificate> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      crewCertificatesService.getAll(opts) as unknown as Promise<{ data: CrewCertificate[]; meta?: any }>,
    []
  );
  return useResourceCollection<CrewCertificate>(fetchFn, options);
}

/**
 * Hook to fetch a single crew certificate by ID
 */
export function useCrewCertificate(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<CrewCertificate>((id) => crewCertificatesService.getById(id, options), id);
}

/**
 * Hook to create a new crew certificate
 */
export function useCreateCrewCertificate(onSuccess?: () => void): MutationState<CrewCertificate> {
  return useCreateResource<CrewCertificate>(crewCertificatesService.create, onSuccess);
}

/**
 * Hook to update an existing crew certificate
 */
export function useUpdateCrewCertificate(onSuccess?: () => void): MutationState<CrewCertificate> {
  return useUpdateResource<CrewCertificate>(crewCertificatesService.update, onSuccess);
}

/**
 * Hook to delete a crew certificate
 */
export function useDeleteCrewCertificate(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(crewCertificatesService.delete, onSuccess);
}

